// src/components/admin/nornsweave/AdminNornsWeaveContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc, getDoc, setDoc, addDoc } from 'firebase/firestore';

const AdminNornsWeaveContext = createContext(null);

// Generate duration options in 5-minute increments from 5 to 120 minutes
export const DURATION_OPTIONS = [];
for (let i = 5; i <= 120; i += 5) {
  DURATION_OPTIONS.push({ value: i, label: `${i} minutes` });
}

// Grade options
export const GRADE_OPTIONS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

// Subject options for middle school (grades 5-8)
export const SUBJECT_OPTIONS = ['ELA', 'Social Studies', 'Math', 'Religion', 'Art', 'PE', 'Spanish', 'Science', 'Advisory'];

// Day options including "Every Day" for recurring items
export const DAY_OPTIONS = [
  { value: 'everyday', label: 'Every Day (Mon-Fri)' },
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' }
];

// Quick closure reason presets
export const CLOSURE_REASONS = [
  { value: 'librarian_meeting', label: 'Librarian Meeting' },
  { value: 'sick_day', label: 'Sick Day' },
  { value: 'personal_day', label: 'Personal Day' },
  { value: 'professional_development', label: 'Professional Development' },
  { value: 'other', label: 'Other' }
];

// Extract last name from full name
export const getLastName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1];
};

// Get z-index for event layering (higher = shows on top)
export const getEventZIndex = (event) => {
  switch(event.eventType) {
    case 'holiday': return 25;
    case 'closure': return 24;
    case 'holyDay': return 22;
    case 'mass': return 22;
    case 'adoration': return 21;
    case 'massBlock': return 20;
    case 'halfDay': return 18;
    case 'halfDayBanner': return 18;
    case 'libraryEvent': return 16;
    case 'booking': return 15;
    case 'fixedClass': return 12;
    case 'block': return 10;
    case 'personal': return 10;
    default: return 10;
  }
};

export function AdminNornsWeaveProvider({ children }) {
  const { currentUser } = useAuth();

  // Core data collections
  const [bookings, setBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [personalEvents, setPersonalEvents] = useState([]);
  const [fixedClasses, setFixedClasses] = useState([]);
  const [aides, setAides] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [closures, setClosures] = useState([]);

  // View management
  const [view, setView] = useState('pending');
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeLibrary, setActiveLibrary] = useState('elementary');

  // Loading state
  const [loading, setLoading] = useState(true);

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Year Setup State
  const [holyDays, setHolyDays] = useState([]);
  const [adorationDays, setAdorationDays] = useState([]);
  const [schoolCalendarEvents, setSchoolCalendarEvents] = useState([]);
  const [massTimeBlocks, setMassTimeBlocks] = useState([]);
  const [saturdayOpenings, setSaturdayOpenings] = useState([]);
  const [isYearSetupLocked, setIsYearSetupLocked] = useState(true);
  const [yearSetupForm, setYearSetupForm] = useState({
    academicYearStart: '',
    academicYearEnd: '',
    schoolStartDate: '',
    schoolEndDate: '',
    massDay: 'Thursday',
    massStartTime: '09:00',
    massEndTime: '10:00'
  });

  // Saturday schedule state
  const [saturdaySchedule, setSaturdaySchedule] = useState({
    enabled: false,
    startTime: '09:00',
    endTime: '13:00',
    dates: []
  });

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    username: '',
    password: '',
    schoolName: '',
    cityName: '',
    yourName: '',
    elementaryStart: '',
    elementaryEnd: '',
    middleSchoolStart: '',
    middleSchoolEnd: '',
    librarianBlocks: [],
    emailEnabled: false,
    librarianEmail: '',
    currentDayType: 'A',
    academicCalendar: [],
    scheduleOverrides: [],
    aideScheduleBlocks: [], // { id, name, startTime, endTime, library, scheduleType: 'regular'|'mass_day' }
    colors: {
      independent: '#22c55e',
      needsLibrarian: '#a855f7',
      fixedClass: '#06b6d4',
      blocked: '#6b7280',
      personal: '#f97316'
    },
    // Recess configuration (array of recess schedules)
    recessSchedules: [],
    // Admin contacts (CC on closures, events, inventory)
    adminContacts: [],
    // Library chairs (parent volunteers who coordinate volunteers)
    libraryChairs: [],
    libraryChairSettings: {
      weeklyDigestEnabled: true,
      weeklyDigestDay: 'Friday',
      weeklyDigestTime: '18:00',
      notifyOnEmergencyClosure: true,
      notifyOnLateTeacherCancel: true,
      lateNoticeDays: 7
    },
    // Library assistant (employee - notified of full-day closures only)
    libraryAssistant: {
      name: '',
      email: '',
      enabled: false,
      notifyOnFullDayClosure: true
    }
  });

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  }, []);

  // Data loading functions
  const loadSettings = useCallback(async () => {
    if (!currentUser) return;
    try {
      const settingsDoc = await getDoc(doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings(data);
        setSettingsForm({
          username: data.username || '',
          password: data.password || 'Library2025',
          schoolName: data.schoolName || '',
          cityName: data.cityName || '',
          yourName: data.yourName || '',
          elementaryStart: data.elementaryHours?.start || '08:00',
          elementaryEnd: data.elementaryHours?.end || '15:00',
          middleSchoolStart: data.middleSchoolHours?.start || '08:00',
          middleSchoolEnd: data.middleSchoolHours?.end || '15:00',
          librarianBlocks: data.librarianBlocks || [],
          emailEnabled: data.emailEnabled || false,
          librarianEmail: data.librarianEmail || '',
          currentDayType: data.currentDayType || 'A',
          academicCalendar: data.academicCalendar || [],
          scheduleOverrides: data.scheduleOverrides || [],
          aideScheduleBlocks: data.aideScheduleBlocks || [],
          colors: data.colors || {
            independent: '#22c55e',
            needsLibrarian: '#a855f7',
            fixedClass: '#06b6d4',
            blocked: '#6b7280',
            personal: '#f97316'
          },
          // Recess configuration (array) - migrate from old single recessSchedule if needed
          recessSchedules: data.recessSchedules || (data.recessSchedule?.enabled ? [{
            id: Date.now().toString(),
            enabled: true,
            startTime: data.recessSchedule.startTime || '11:30',
            endTime: data.recessSchedule.endTime || '12:00',
            dayOfWeek: data.recessSchedule.dayOfWeek || 'everyday',
            grades: data.recessSchedule.grades || [],
            description: data.recessSchedule.description || 'Library Recess'
          }] : []),
          // Admin contacts
          adminContacts: data.adminContacts || [],
          // Library chairs
          libraryChairs: data.libraryChairs || [],
          libraryChairSettings: data.libraryChairSettings || {
            weeklyDigestEnabled: true,
            weeklyDigestDay: 'Friday',
            weeklyDigestTime: '18:00',
            notifyOnEmergencyClosure: true,
            notifyOnLateTeacherCancel: true,
            lateNoticeDays: 7
          },
          // Library assistant
          libraryAssistant: data.libraryAssistant || {
            name: '',
            email: '',
            enabled: false,
            notifyOnFullDayClosure: true
          }
        });
      } else {
        // If settings document doesn't exist, set empty settings object
        // This prevents null checks from failing in child components
        setSettings({});
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      // On error, set empty settings object instead of leaving as null
      setSettings({});
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadBookings = useCallback(async () => {
    if (!currentUser) return;
    try {
      const bookingsRef = collection(db, 'users', currentUser.uid, 'nornsWeaveBookings');
      const snapshot = await getDocs(bookingsRef);
      const bookingsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookings(bookingsList);
    } catch (err) {
      console.error('Error loading bookings:', err);
    }
  }, [currentUser]);

  const loadPendingBookings = useCallback(async () => {
    if (!currentUser) return;
    try {
      const pendingRef = collection(db, 'users', currentUser.uid, 'nornsWeavePending');
      const snapshot = await getDocs(pendingRef);
      const pendingList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingBookings(pendingList);
    } catch (err) {
      console.error('Error loading pending bookings:', err);
    }
  }, [currentUser]);

  const loadPersonalEvents = useCallback(async () => {
    if (!currentUser) return;
    try {
      const eventsRef = collection(db, 'users', currentUser.uid, 'nornsWeaveEvents');
      const snapshot = await getDocs(eventsRef);
      const eventsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPersonalEvents(eventsList);
    } catch (err) {
      console.error('Error loading personal events:', err);
    }
  }, [currentUser]);

  const loadFixedClasses = useCallback(async () => {
    if (!currentUser) return;
    try {
      const classesRef = collection(db, 'users', currentUser.uid, 'nornsWeaveClasses');
      const snapshot = await getDocs(classesRef);
      const classesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFixedClasses(classesList);
    } catch (err) {
      console.error('Error loading fixed classes:', err);
    }
  }, [currentUser]);

  const loadAides = useCallback(async () => {
    if (!currentUser) return;
    try {
      const aidesRef = collection(db, 'users', currentUser.uid, 'nornsWeaveAides');
      const snapshot = await getDocs(aidesRef);
      const aidesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAides(aidesList);
    } catch (err) {
      console.error('Error loading aides:', err);
    }
  }, [currentUser]);

  const loadTeachers = useCallback(async () => {
    if (!currentUser) return;
    try {
      const teachersRef = collection(db, 'users', currentUser.uid, 'nornsWeaveTeachers');
      const snapshot = await getDocs(teachersRef);
      const teachersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeachers(teachersList);
    } catch (err) {
      console.error('Error loading teachers:', err);
    }
  }, [currentUser]);

  const loadClosures = useCallback(async () => {
    if (!currentUser) return;
    try {
      const closuresSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveClosures'));
      const closuresData = closuresSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClosures(closuresData);
    } catch (err) {
      console.error('Error loading closures:', err);
    }
  }, [currentUser]);

  const loadYearSetupData = useCallback(async () => {
    if (!currentUser) return;
    try {
      // Load holy days
      const holyDaysSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveHolyDays'));
      const holyDaysData = holyDaysSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setHolyDays(holyDaysData);

      // Load adoration days
      const adorationSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveAdorationDays'));
      const adorationData = adorationSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAdorationDays(adorationData);

      // Load school calendar events
      const calendarSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveSchoolCalendar'));
      const calendarData = calendarSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSchoolCalendarEvents(calendarData);

      // Load mass time blocks
      const blocksSnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveMassTimeBlocks'));
      const blocksData = blocksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMassTimeBlocks(blocksData);

      // Load Saturday openings
      const saturdaySnap = await getDocs(collection(db, 'users', currentUser.uid, 'nornsWeaveSaturdayOpenings'));
      const saturdayData = saturdaySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSaturdayOpenings(saturdayData);

      // Load academic year settings from main settings
      const settingsDoc = await getDoc(doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        console.log('📖 Loading year setup data from Firebase:', data);

        if (data.academicYear) {
          console.log('Found academicYear:', data.academicYear);
          setYearSetupForm({
            academicYearStart: data.academicYear.startDate || '',
            academicYearEnd: data.academicYear.endDate || '',
            schoolStartDate: data.academicYear.schoolStartDate || '',
            schoolEndDate: data.academicYear.schoolEndDate || '',
            massDay: data.massSchedule?.regularDay || 'Thursday',
            massStartTime: data.massSchedule?.startTime || '09:00',
            massEndTime: data.massSchedule?.endTime || '10:00'
          });
          setIsYearSetupLocked(data.academicYear.isLocked !== false);
        } else {
          console.log('⚠️ No academicYear data found in settings');
        }
        // Load Saturday schedule (config only, dates are in nornsWeaveSaturdayOpenings collection)
        if (data.saturdaySchedule) {
          console.log('Found saturdaySchedule:', data.saturdaySchedule);
          setSaturdaySchedule({
            enabled: data.saturdaySchedule.enabled || false,
            startTime: data.saturdaySchedule.startTime || '09:00',
            endTime: data.saturdaySchedule.endTime || '13:00'
          });
        } else {
          console.log('⚠️ No saturdaySchedule data found in settings');
        }
      } else {
        console.log('⚠️ Settings document does not exist');
      }
    } catch (err) {
      console.error('Error loading year setup data:', err);
    }
  }, [currentUser]);

  // Load all data on mount
  useEffect(() => {
    if (currentUser) {
      loadSettings();
      loadBookings();
      loadPendingBookings();
      loadPersonalEvents();
      loadFixedClasses();
      loadAides();
      loadClosures();
      loadTeachers();
      loadYearSetupData();
    }
  }, [currentUser, loadSettings, loadBookings, loadPendingBookings, loadPersonalEvents, loadFixedClasses, loadAides, loadClosures, loadTeachers, loadYearSetupData]);

  // Helper to format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to check if a Saturday is open
  // Supports both old format (array of strings) and new format (array of objects with date/description)
  const isSaturdayOpen = useCallback((dateStr) => {
    if (!saturdaySchedule.enabled) return false;
    const dates = saturdaySchedule.dates || [];
    return dates.some(d => (typeof d === 'string' ? d : d.date) === dateStr);
  }, [saturdaySchedule]);

  // Get description for a specific Saturday date
  const getSaturdayDescription = useCallback((dateStr) => {
    if (!saturdaySchedule.enabled) return '';
    const dates = saturdaySchedule.dates || [];
    const found = dates.find(d => (typeof d === 'string' ? d : d.date) === dateStr);
    if (!found) return '';
    return typeof found === 'string' ? '' : (found.description || '');
  }, [saturdaySchedule]);

  // Get Saturday date for a given week offset
  const getSaturdayForWeek = useCallback((offset = 0) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + (offset * 7));
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    return formatLocalDate(saturday);
  }, []);

  // Check if current week has an open Saturday
  const weekHasOpenSaturday = useCallback((offset = 0) => {
    const saturdayDate = getSaturdayForWeek(offset);
    return isSaturdayOpen(saturdayDate);
  }, [getSaturdayForWeek, isSaturdayOpen]);

  // Get Saturday hours
  const getSaturdayHours = useCallback(() => {
    return {
      start: saturdaySchedule.startTime || '09:00',
      end: saturdaySchedule.endTime || '13:00'
    };
  }, [saturdaySchedule]);

  // Update Saturday schedule and persist to Firebase
  const updateSaturdaySchedule = useCallback(async (newSchedule) => {
    if (!currentUser) return;
    try {
      // Update local state immediately
      setSaturdaySchedule(newSchedule);

      // Persist to Firebase
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave');
      await setDoc(settingsRef, {
        saturdaySchedule: newSchedule
      }, { merge: true });
    } catch (err) {
      console.error('Error saving Saturday schedule:', err);
    }
  }, [currentUser]);

  // Helper functions
  const getWeekDates = useCallback((offset = 0, includeSaturday = null) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + (offset * 7));

    // Determine if Saturday should be included
    const saturdayDate = new Date(monday);
    saturdayDate.setDate(monday.getDate() + 5);
    const saturdayStr = formatLocalDate(saturdayDate);
    const showSaturday = includeSaturday !== null ? includeSaturday : isSaturdayOpen(saturdayStr);

    const numDays = showSaturday ? 6 : 5;
    const dates = [];
    for (let i = 0; i < numDays; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(formatLocalDate(date));
    }
    return dates;
  }, [isSaturdayOpen]);

  const getDayName = useCallback((dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
  }, []);

  const getDayOfWeekFromDate = useCallback((dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
  }, []);

  const getLibraryHours = useCallback((library) => {
    if (library === 'elementary') {
      return {
        start: settings?.elementaryHours?.start || settingsForm.elementaryStart || '07:30',
        end: settings?.elementaryHours?.end || settingsForm.elementaryEnd || '16:00'
      };
    } else {
      return {
        start: settings?.middleSchoolHours?.start || settingsForm.middleSchoolStart || '08:00',
        end: settings?.middleSchoolHours?.end || settingsForm.middleSchoolEnd || '15:30'
      };
    }
  }, [settings, settingsForm]);

  const timeToMinutes = useCallback((time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }, []);

  // Email notification helper functions

  // Get teachers for a specific recess schedule
  const getTeachersForRecess = useCallback((recess) => {
    if (!recess?.enabled) return [];
    const recessGrades = recess.grades || [];
    return teachers.filter(t => recessGrades.includes(t.grade));
  }, [teachers]);

  // Get all teachers across all enabled recesses (for notification count)
  const getRecessTeachers = useCallback(() => {
    const schedules = settingsForm.recessSchedules || [];
    const enabledSchedules = schedules.filter(r => r.enabled);
    if (enabledSchedules.length === 0) return [];
    const allGrades = [...new Set(enabledSchedules.flatMap(r => r.grades || []))];
    return teachers.filter(t => allGrades.includes(t.grade));
  }, [settingsForm.recessSchedules, teachers]);

  // Get all enabled recess schedules affected by a closure
  const getAffectedRecesses = useCallback((startTime, endTime, library, timeType) => {
    const schedules = settingsForm.recessSchedules || [];
    if (library !== 'both') return [];

    return schedules.filter(recess => {
      if (!recess.enabled) return false;
      // Full day closure always affects recess
      if (timeType === 'full') return true;
      // For partial closure, check time overlap
      return startTime < recess.endTime && endTime > recess.startTime;
    });
  }, [settingsForm.recessSchedules]);

  // Check if a closure affects any recess time (backward compat)
  const doesClosureAffectRecess = useCallback((startTime, endTime, library, timeType) => {
    return getAffectedRecesses(startTime, endTime, library, timeType).length > 0;
  }, [getAffectedRecesses]);

  // Get aides for a specific day type (A or B)
  const getAidesForDayType = useCallback((dayType) => {
    if (!dayType || dayType === 'na') return [];
    return aides.filter(a => a.dayType === dayType);
  }, [aides]);

  // Get admin contacts that should be notified for a specific type
  const getAdminContactsForType = useCallback((notificationType) => {
    // notificationType: 'closures', 'events', or 'inventory'
    return (settingsForm.adminContacts || []).filter(c =>
      c.notifyOn?.includes(notificationType)
    );
  }, [settingsForm.adminContacts]);

  // Get library chairs (all of them - they all get same notifications)
  const getLibraryChairs = useCallback(() => {
    return settingsForm.libraryChairs || [];
  }, [settingsForm.libraryChairs]);

  // Check if a cancellation is "late" (less than configured days notice)
  const isLateCancellation = useCallback((cancellationDate, classDate) => {
    const lateNoticeDays = settingsForm.libraryChairSettings?.lateNoticeDays || 7;
    const cancellation = new Date(cancellationDate);
    const classDay = new Date(classDate);
    const diffTime = classDay - cancellation;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays < lateNoticeDays;
  }, [settingsForm.libraryChairSettings]);

  // Get library assistant if enabled
  const getLibraryAssistant = useCallback(() => {
    const assistant = settingsForm.libraryAssistant;
    if (!assistant?.enabled || !assistant?.email) return null;
    return assistant;
  }, [settingsForm.libraryAssistant]);

  // Check if closure is full-day (for library assistant notification)
  const isFullDayClosure = useCallback((timeType) => {
    return timeType === 'full';
  }, []);

  // Validate email format
  const isValidEmail = useCallback((email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  // Computed values
  const weekDates = getWeekDates(weekOffset);
  const thisWeek = getWeekDates(0);
  const thisWeekBookings = bookings.filter(b => thisWeek.includes(b.date));

  const value = {
    // Data
    bookings,
    setBookings,
    pendingBookings,
    setPendingBookings,
    personalEvents,
    setPersonalEvents,
    fixedClasses,
    setFixedClasses,
    aides,
    setAides,
    teachers,
    setTeachers,
    settings,
    setSettings,
    closures,
    setClosures,

    // Year setup data
    holyDays,
    setHolyDays,
    adorationDays,
    setAdorationDays,
    schoolCalendarEvents,
    setSchoolCalendarEvents,
    massTimeBlocks,
    setMassTimeBlocks,
    saturdayOpenings,
    setSaturdayOpenings,
    isYearSetupLocked,
    setIsYearSetupLocked,
    yearSetupForm,
    setYearSetupForm,

    // Saturday schedule (configuration)
    saturdaySchedule,
    setSaturdaySchedule,
    updateSaturdaySchedule,

    // Settings form
    settingsForm,
    setSettingsForm,

    // View management
    view,
    setView,
    weekOffset,
    setWeekOffset,
    activeLibrary,
    setActiveLibrary,

    // State
    loading,
    notification,
    showNotification,

    // Data loading functions
    loadSettings,
    loadBookings,
    loadPendingBookings,
    loadPersonalEvents,
    loadFixedClasses,
    loadAides,
    loadTeachers,
    loadClosures,
    loadYearSetupData,

    // Helper functions
    getWeekDates,
    getDayName,
    getDayOfWeekFromDate,
    getLibraryHours,
    timeToMinutes,
    isSaturdayOpen,
    getSaturdayForWeek,
    getSaturdayDescription,
    weekHasOpenSaturday,
    getSaturdayHours,

    // Email notification helpers
    getRecessTeachers,
    getTeachersForRecess,
    getAffectedRecesses,
    doesClosureAffectRecess,
    getAidesForDayType,
    getAdminContactsForType,
    getLibraryChairs,
    isLateCancellation,
    getLibraryAssistant,
    isFullDayClosure,
    isValidEmail,

    // Computed values
    weekDates,
    thisWeek,
    thisWeekBookings,

    // Current user
    currentUser
  };

  return (
    <AdminNornsWeaveContext.Provider value={value}>
      {children}
    </AdminNornsWeaveContext.Provider>
  );
}

export function useAdminNornsWeave() {
  const context = useContext(AdminNornsWeaveContext);
  if (!context) {
    throw new Error('useAdminNornsWeave must be used within an AdminNornsWeaveProvider');
  }
  return context;
}

export default AdminNornsWeaveContext;
